import React from 'react'
import { Stack, Typography } from '@mui/material'

import { headerWithTerm } from 'components'
import { COMPANY_ACTIVATED_STATUSES } from 'constants/statuses'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { date } from 'utils'

/** VRG-718 — what both lists know about a company's activations (see the API's billable-activations.ts). */
export interface CompanyActivationFigures {
  /** ISO timestamp of the first billable activation, or null. */
  first_activated_at: string | null
  activations_count: number
  /** Billable activations in the last 30 days. */
  activations_recent: number
}

const time = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : null)

/**
 * VRG-718 — the "Activated" and "Activations" columns, shared by Accounts → Companies and
 * Sales → Companies so the two lists say the same thing the same way (§36: extracted on the
 * second use rather than copied).
 *
 * - Activated: the cell is the date of the first billable activation (a MILESTONE, not a deal
 *   stage); the facet is yes/no; it sorts on the date with never-activated last both ways.
 * - Activations: the billable count, with the last 30 days beside it; the facet is the shared
 *   range slider.
 *
 * The field names (`first_activated_at`, `activations_count`) are the API's sort fields, so a
 * server-sorted table sends them as they are.
 *
 * `get` returning null/undefined means "no platform company to ask" (a Sales company not yet
 * linked): the cells show a dash and the facet reads Not activated.
 */
export const companyActivationColumns = <R,>(
  get: (row: R) => CompanyActivationFigures | null | undefined,
  opts: { variant?: 'body2' | 'body3' } = {},
) => {
  const variant = opts.variant ?? 'body2'
  const dash = (
    <Typography variant={variant} color="text.disabled">
      —
    </Typography>
  )
  return [
    {
      field: 'first_activated_at',
      headerName: 'Activated',
      renderHeader: headerWithTerm('Activated', 'company-activated'),
      type: 'statusChip',
      options: statusFilterOptions(COMPANY_ACTIVATED_STATUSES),
      // The FACET value. The sort reads the date through the comparator below.
      valueGetter: (_v: unknown, row: R) => (get(row)?.first_activated_at ? 'activated' : 'not_activated'),
      // Client-sorted tables (Sales) sort by date, never-activated LAST in both directions. MUI
      // uses a `getSortComparator` result as-is (it does not negate it for desc, unlike
      // `sortComparator`), so the direction is applied here, to the dated rows only.
      getSortComparator: (direction: 'asc' | 'desc' | null | undefined) => (_a: unknown, _b: unknown, p1: any, p2: any) => {
        const a = time(get(p1.api.getRow(p1.id))?.first_activated_at)
        const b = time(get(p2.api.getRow(p2.id))?.first_activated_at)
        if (a === null && b === null) return 0
        if (a === null) return 1
        if (b === null) return -1
        return direction === 'desc' ? b - a : a - b
      },
      width: 150,
      renderCell: ({ row }: { row: R }) => {
        const at = get(row)?.first_activated_at
        return (
          <Stack justifyContent="center" height="100%">
            {at ? <Typography variant={variant}>{date(at)}</Typography> : dash}
          </Stack>
        )
      },
    },
    {
      field: 'activations_count',
      headerName: 'Activations',
      renderHeader: headerWithTerm('Activations', 'company-activations'),
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_v: unknown, row: R) => get(row)?.activations_count ?? 0,
      width: 168,
      renderCell: ({ row }: { row: R }) => {
        const a = get(row)
        if (!a) return <Stack justifyContent="center" height="100%">{dash}</Stack>
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" height="100%" sx={{ minWidth: 0 }}>
            <Typography variant={variant}>{a.activations_count}</Typography>
            {a.activations_recent > 0 && (
              <Typography variant={variant} color="text.secondary" noWrap>
                · {a.activations_recent} in 30d
              </Typography>
            )}
          </Stack>
        )
      },
    },
  ]
}
